// Message search (GET /me/messages/search): pure helpers for the search box,
// result snippets and jumping to a result in its thread.
import type { Thread } from "./chat";

/** Typing pauses this long before a search goes out. */
export const SEARCH_DEBOUNCE_MS = 300;
/** As the server takes it (trimmed). */
export const SEARCH_MAX = 200;

/**
 * The words of a query, lowercased: what the server matches, each as the
 * start of a word. Empty when there is nothing to search for (only spaces or
 * punctuation), so no request goes out.
 */
export function searchWords(query: string): string[] {
  return [...new Set((query.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []).filter(Boolean))];
}

/** The trimmed query to send, or null when it has no words or is too long. */
export function searchText(query: string): string | null {
  const text = query.trim();
  return searchWords(text).length > 0 && [...text].length <= SEARCH_MAX ? text : null;
}

export interface Segment {
  text: string;
  match: boolean;
}

/** Splits `text` into runs, marking the starts of words that begin with one of `words` (longest first). */
export function highlight(text: string, words: readonly string[]): Segment[] {
  const sorted = [...words].sort((a, b) => b.length - a.length);
  const segments: Segment[] = [];
  const push = (part: string, match: boolean) => {
    if (!part) return;
    const last = segments.at(-1);
    if (last && last.match === match) last.text += part;
    else segments.push({ text: part, match });
  };
  let at = 0;
  for (const word of text.matchAll(/[\p{L}\p{N}]+/gu)) {
    const lower = word[0].toLowerCase();
    const hit = sorted.find((w) => lower.startsWith(w));
    if (!hit) continue;
    // Lowercasing can change lengths (rarely): then mark the whole word.
    const length = lower.length === word[0].length ? hit.length : word[0].length;
    push(text.slice(at, word.index), false);
    push(text.slice(word.index, word.index + length), true);
    at = word.index + length;
  }
  push(text.slice(at), false);
  return segments;
}

/**
 * A one-line excerpt of `body` of at most `max` characters (code points)
 * around its first match, with "…" where it was cut, split for highlighting.
 */
export function snippet(body: string, words: readonly string[], max = 120): Segment[] {
  const line = body.replace(/\s+/g, " ").trim();
  const chars = [...line];
  if (chars.length <= max) return highlight(line, words);
  // Where the first match starts, in code points; the excerpt starts a little before it.
  let matchAt = 0;
  for (const segment of highlight(line, words)) {
    if (segment.match) break;
    matchAt += [...segment.text].length;
  }
  let start = Math.max(0, matchAt - Math.floor(max / 4));
  if (start > 0) start = Math.min(start, chars.length - (max - 1));
  let end = start + (start > 0 ? max - 1 : max);
  if (end < chars.length) end -= 1;
  const text = `${start > 0 ? "…" : ""}${chars.slice(start, end).join("")}${end < chars.length ? "…" : ""}`;
  return highlight(text, words);
}

/**
 * What jumping to message `seq` needs in a thread: it is `shown`, older pages
 * must `load` first, or it is `missing` (unsent, cleared or never there).
 * `wait` while the newest page has not loaded.
 */
export function jumpStep(thread: Thread | undefined, seq: number): "shown" | "load" | "missing" | "wait" {
  if (!thread || !thread.loaded) return "wait";
  if (thread.messages.some((message) => message.seq === seq)) return "shown";
  const first = thread.messages[0];
  if (thread.hasOlder && (!first || first.seq > seq)) return "load";
  return "missing";
}
